import { PlaybackList } from '@piggybank/core';
import {
  Checkbox,
  CurrencyInput,
  DateInput,
  FilterableSelect,
  IconTile,
  LinkTileInput,
  NumberInput,
  Radio,
  Select,
  TextArea,
  TextInput,
  TileGroup,
  YesNoRadio,
} from '@piggybank/form';
import entries from 'lodash/entries';
import first from 'lodash/first';
import get from 'lodash/get';
import has from 'lodash/has';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import last from 'lodash/last';
import pick from 'lodash/pick';
import size from 'lodash/size';
import values from 'lodash/values';
import T from 'prop-types';
import React from 'react';
import * as builtinCustomComponents from './CustomComponents';
import { DynamicFormHint } from './DynamicFormHint';
import DynamicFormReadOnlyField from './DynamicFormReadOnlyField';
import { DynamicFormTooltip } from './DynamicFormTooltip';
import { renderMarkdown } from './Markdown';
import { DF_KEYWORD_CUSTOM_INPUT } from './constants';
import { Optional, useProductCodes, useTranslations } from './context';
import { pairValuesWithLabels, transformEnumsAtSchemaNode } from './operators';
import {
  excludeOptionsFromArray,
  excludeOptionsFromObject,
  formNameFromKeyPath,
  getCustomComponentDefinition,
  getIcon,
  localizationKeyPath,
  reducei18n,
  requiredArg,
  resolveTranslationRefs,
} from './utils';

const allowedInputComponents = {
  Checkbox,
  CurrencyInput,
  DateInput,
  FilterableSelect,
  LinkTileInput,
  TextInput,
  TextArea,
  TileGroup,
  Select,
  NumberInput,
  Radio,
  YesNoRadio,
};

const autocompleteMappings = {
  email: 'email',
  fullName: 'name',
  firstNames: 'given-name',
  lastName: 'family-name',
  gender: 'sex',
  telephone: 'tel-national',
  mobileNumber: 'tel-national',
  addressLine1: 'address-line1',
  addressLine2: 'address-line2',
  addressLine3: 'address-line3',
  postcode: 'postal-code',
  occupation: 'organization-title',
  businessName: 'organization',
};

export const mapKeyPathToAutoCompleteValue = (
  keyPath = {},
  mappings = autocompleteMappings
) => get(mappings, last(values(keyPath)), 'on');

const excludedFieldSetCustomComponents = ['CurrencyInput'];

const fieldSetComponents = [
  'YesNoRadio',
  'Radio',
  'DateInput',
  'TileGroup',
  'TelephoneInput',
];

export const getComponentName = ({
  schemaNode: { enum: enumeration, format, type },
}) => {
  if (type === 'boolean') return 'YesNoRadio';
  if (enumeration) return 'Select';
  if (type === 'string') return 'TextInput';
  if (type === 'integer' || type === 'number') return 'NumberInput';

  throw new Error(
    `Can't map a component to ${JSON.stringify({
      type,
      format,
      enum: enumeration,
    })}`
  );
};

export const getPropsForInput = ({
  componentName,
  context,
  excludeOptions,
  formValues,
  keyPath,
  productCodes,
  readOnly,
  renderChildren,
  schemaNode,
  translations,
}) => {
  const keyPathProps = get(translations, localizationKeyPath(keyPath));

  const resolveRefs = property =>
    resolveTranslationRefs(get(keyPathProps, property), translations);

  switch (componentName) {
    case 'LinkTileInput': {
      const { href, label } = keyPathProps;

      return {
        heading: label,
        href: isArray(href) ? first(reducei18n(href, productCodes)) : href,
      };
    }

    case 'FilterableSelect': {
      const optionLabels = resolveRefs('optionLabels');

      const combineProperties = (acc, label, value) => [
        ...acc,
        {
          label: label || value,
          value,
        },
      ];

      const items = pairValuesWithLabels(
        {
          labels: optionLabels,
          values: transformEnumsAtSchemaNode(schemaNode),
        },
        [],
        combineProperties
      );

      const customFilter = (items, input) => {
        if (!input) return items;
        return items.filter(item => {
          const match = item.label.toLowerCase().indexOf(input.toLowerCase());
          return (
            match === 0 || (match > 0 && item.label[match - 1].match(/[^A-Z]/i))
          );
        });
      };

      const customSuggestionText = (label, inputValue) => (
        <>
          {label
            .split(new RegExp(`(${inputValue})`, 'gi'))
            .map((part, i) =>
              part.toLowerCase() === inputValue.toLowerCase() ? (
                <b key={i}>{part}</b>
              ) : (
                part
              )
            )}
        </>
      );

      return {
        items: readOnly
          ? items
          : excludeOptionsFromArray(items, excludeOptions),
        filterItems: customFilter,
        hideInlineAutocomplete: true,
        renderSuggestionText: customSuggestionText,
      };
    }

    case 'Select': {
      const optionLabels = resolveRefs('optionLabels');

      let placeholder =
        resolveRefs('placeholder') ||
        get(translations, 'global.select.placeholder', false);

      const combineProperties = (acc, label, value) => [
        ...acc,
        {
          label: label || value,
          value,
        },
      ];

      const options = pairValuesWithLabels(
        {
          labels: optionLabels,
          values: transformEnumsAtSchemaNode(schemaNode),
        },
        [],
        combineProperties
      );

      return {
        options: readOnly
          ? options
          : excludeOptionsFromArray(options, excludeOptions),
        ...(placeholder !== false ? { placeholder } : {}),
      };
    }

    case 'Radio': {
      const optionLabels = resolveRefs('optionLabels');

      const combineProperties = (acc, label, value) => ({
        ...acc,
        [value]: renderMarkdown(
          label,
          { ...formValues, ...productCodes },
          { translationPath: [...localizationKeyPath(keyPath), value] }
        ),
      });

      const options = pairValuesWithLabels(
        {
          labels: optionLabels,
          values: transformEnumsAtSchemaNode(schemaNode),
        },
        {},
        combineProperties
      );

      return {
        options: readOnly
          ? options
          : excludeOptionsFromObject(options, excludeOptions),
      };
    }

    case 'YesNoRadio': {
      const optionLabels = resolveRefs('optionLabels');

      const cb = (acc, label, value) => ({
        ...acc,
        ...(label ? { [value]: label } : {}),
      });

      const textMap = pairValuesWithLabels(
        {
          labels: optionLabels,
          values: ['yes', 'no'],
        },
        {},
        cb
      );

      return {
        ...(size(textMap) ? { textMap } : {}),
        values: {
          no: get(schemaNode, 'enum[1]', false),
          yes: get(schemaNode, 'enum[0]', true),
        },
      };
    }

    case 'TelephoneInput': {
      const autoComplete = mapKeyPathToAutoCompleteValue(keyPath);

      return { autoComplete };
    }

    case 'TextInput': {
      const placeholder =
        resolveRefs('placeholder') ||
        get(translations, 'global.textInput.placeholder', false);

      const defaultProps = {
        autoComplete: mapKeyPathToAutoCompleteValue(keyPath),
        ...pick(schemaNode, ['maxLength']),
      };

      return {
        ...defaultProps,
        ...(typeof schemaNode.displayType !== 'undefined'
          ? { type: schemaNode.displayType }
          : {}),
        ...(placeholder !== false ? { placeholder } : {}),
      };
    }

    case 'NumberInput': {
      const maxLength =
        get(
          schemaNode,
          'maximum',
          get(schemaNode, 'exclusiveMaximum', '')
        ).toString().length || '';

      return {
        autoComplete: mapKeyPathToAutoCompleteValue(keyPath),
        ...(maxLength ? { maxLength } : {}),
      };
    }

    case 'Checkbox': {
      const props = {
        context,
        formValues: {
          ...formValues,
          ...productCodes,
        },
      };

      const labelTooltipAndHint = (
        <>
          {renderMarkdown(get(keyPathProps, 'label', ''), props.formValues, {
            translationPath: [...localizationKeyPath(keyPath), 'label'],
          })}
          <Optional>
            <DynamicFormTooltip
              formValues={formValues}
              path={localizationKeyPath(keyPath)}
            />
          </Optional>
          <Optional>
            <DynamicFormHint {...props} />
          </Optional>
        </>
      );

      const checked = get(formValues, formNameFromKeyPath(keyPath), false);
      return { children: labelTooltipAndHint, checked };
    }

    case 'TextArea': {
      const placeholder =
        resolveRefs('placeholder') ||
        get(translations, 'global.textArea.placeholder', false);

      const defaultProps = {
        ...pick(schemaNode, ['maxLength']),
      };

      return {
        ...defaultProps,
        ...(placeholder !== false ? { placeholder } : {}),
      };
    }

    case 'TileGroup': {
      let values = transformEnumsAtSchemaNode(
        schemaNode.type === 'string' ? schemaNode : schemaNode.items
      );

      const optionLabels = resolveRefs('optionLabels');

      const combineProperties = (acc, { icon, label }, value) => {
        const option = {
          icon,
          label: renderMarkdown(
            label,
            { ...formValues, ...productCodes },
            { translationPath: [...localizationKeyPath(keyPath), value] }
          ),
          value,
        };

        acc.push(option);
        return acc;
      };

      const options = excludeOptionsFromArray(
        pairValuesWithLabels(
          {
            labels: optionLabels,
            values,
          },
          [],
          combineProperties
        ),
        excludeOptions
      );

      const children = options.map(({ icon, label, value }, index) => (
        <IconTile icon={getIcon(icon)} key={'value' + index} value={value}>
          {label}
        </IconTile>
      ));

      return {
        children,
        ...(schemaNode.type === 'array'
          ? {
              multiSelect: true,
            }
          : {}),
      };
    }

    case 'CurrencyInput': {
      return {
        formValues,
        renderChildren,
      };
    }

    default: {
      return {};
    }
  }
};

export const getInputMetadata = ({ schemaNode = {}, context = {} }) => {
  if (!has(schemaNode, 'type')) {
    throw new Error(`Input Component is missing type definition`);
  }

  const componentOverride = getCustomComponentDefinition(
    schemaNode,
    DF_KEYWORD_CUSTOM_INPUT
  );

  let componentName;
  let customComponentName;

  try {
    customComponentName = get(
      componentOverride,
      'customComponentName',
      componentOverride
    );

    componentName =
      typeof customComponentName === 'string'
        ? customComponentName
        : getComponentName({ schemaNode, context });
  } catch (_) {
    componentName = null;
  }

  const componentProps = get(componentOverride, 'customComponentProps', {});

  const isFieldset =
    includes(fieldSetComponents, componentName) ||
    (schemaNode.type === 'object' &&
      !includes(excludedFieldSetCustomComponents, componentName));

  return {
    componentName,
    componentProps,
    isFieldset,
    ...(componentName === 'LinkTileInput' || componentName === 'Checkbox'
      ? {
          componentRendersOwnLabel: true,
        }
      : {}),
  };
};

export const DynamicFormInput = ({
  context = requiredArg('context'),
  validate = requiredArg('validate'),
  schemaNode = requiredArg('schemaNode'),
  setMetadataForFieldName = requiredArg('setMetadataForFieldName'),
  getMetadataForFieldName = requiredArg('getMetadataForFieldName'),
  customComponents = {},
  componentOverride,
  readOnly,
  formValues,
  renderChildren,
}) => {
  const translations = useTranslations();
  const productCodes = useProductCodes();
  const { keyPath, excludeOptions = [] } = context;

  const { componentName, componentProps } = getInputMetadata({
    context,
    schemaNode,
    componentOverride,
  });

  const CustomComponent =
    customComponents[componentName] || builtinCustomComponents[componentName];

  const Input = CustomComponent || allowedInputComponents[componentName];

  const inputProps = {
    ...getPropsForInput({
      componentName,
      translations,
      productCodes,
      keyPath,
      schemaNode,
      formValues,
      renderChildren,
      excludeOptions,
      readOnly,
      context,
    }),
    ...componentProps,
    ...(CustomComponent
      ? {
          context,
          validate,
        }
      : {}),
  };

  if (!Input)
    throw new Error(
      `Custom Input Component "${componentName}" was not found in customComponents registry`
    );

  if (typeof setMetadataForFieldName === 'function') {
    setMetadataForFieldName(formNameFromKeyPath(keyPath).join('.'), {
      context,
      componentName,
      componentProps: inputProps,
    });
  }

  if (componentName === 'Radio' && !readOnly) {
    const { options, ...rest } = inputProps;

    return entries(options).map(([value, children]) => (
      <Input {...rest} key={value} value={value}>
        {children}
      </Input>
    ));
  }

  if (readOnly) {
    return (
      <PlaybackList>
        <DynamicFormReadOnlyField
          {...{
            formValues,
            formData: get(formValues, formNameFromKeyPath(keyPath), ''),
            metaData: {
              ...getMetadataForFieldName(
                [
                  keyPath.pageName,
                  keyPath.inputName,
                  keyPath.index,
                  keyPath.inputName2,
                  keyPath.inputName3,
                ]
                  .filter(Boolean)
                  .join('.')
              ),
              context: context,
            },
            readOnly,
            validate,
            formatter: get(CustomComponent, 'reviewFormatter', null),
          }}
        />
      </PlaybackList>
    );
  }

  return <Input {...inputProps} />;
};

DynamicFormInput.propTypes = {
  schemaNode: T.shape({
    type: T.string,
    format: T.string,
    enum: T.arrayOf(T.string),
  }),
  translations: T.object,
  context: T.shape({
    keyPath: T.shape({
      pageName: T.string,
      sectionName: T.string,
      inputName: T.string,
    }),
  }),
  customComponents: T.object,
  componentOverride: T.oneOfType([
    T.shape({
      customComponentName: T.string,
      customComponentProps: T.object,
    }),
    T.string,
  ]),
  validate: T.func,
  setMetadataForFieldName: T.func,
  getMetadataForFieldName: T.func,
  formValues: T.object,
  readOnly: T.bool,
  renderChildren: T.func,
};
